import { CreditCard, Barcode, QrCode, Copy, Check } from "lucide-react";
import { useState, useEffect } from "react";

const PaymentSection = () => {
  // Estados para controle dos métodos de pagamento
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("credit-card");
  const [copiedPix, setCopiedPix] = useState(false);
  
  // Estados específicos para cartão de crédito
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [savedCards, setSavedCards] = useState([
    {
      id: "1",
      type: "visa",
      last4: "1234",
      expiry: "12/25",
      name: "João Silva",
    },
    {
      id: "2",
      type: "mastercard",
      last4: "5678",
      expiry: "08/26",
      name: "João Silva",
    },
  ]);
  const [selectedCard, setSelectedCard] = useState("1");

  // Estados para simulação de Pix
  const [pixCode] = useState("123e4567-e89b-12d3-a456-426614174000");
  const [pixExpiry, setPixExpiry] = useState(30 * 60); // 30 minutos em segundos

  // Estados para simulação de boleto
  const [boletoCode] = useState("12345.67890 12345.678901 12345.678901 1 12345678901234");
  const [boletoExpiryDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 3); // 3 dias úteis
    return date.toLocaleDateString('pt-BR');
  });

  // Efeito para contagem regressiva do Pix
  useEffect(() => {
    if (selectedPaymentMethod === "pix" && pixExpiry > 0) {
      const timer = setInterval(() => {
        setPixExpiry(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [selectedPaymentMethod, pixExpiry]);

  // Formatar tempo restante do Pix
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Manipulador para adicionar novo cartão
  const handleAddCard = (newCard) => {
    const card = {
      id: Date.now().toString(),
      type: newCard.cardType,
      last4: newCard.cardNumber.slice(-4),
      expiry: newCard.expiry,
      name: newCard.cardName,
    };
    setSavedCards([...savedCards, card]);
    setSelectedCard(card.id);
    setShowNewCardForm(false);
  };

  // Copiar chave Pix
  const copyPixCode = () => {
    navigator.clipboard.writeText(pixCode);
    setCopiedPix(true);
    setTimeout(() => setCopiedPix(false), 2000);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center mb-4">
        <CreditCard className="h-5 w-5 text-gray-500 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900">
          Método de Pagamento
        </h2>
      </div>

      <div className="space-y-4">
        {/* Cartão de Crédito */}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div 
            className="flex items-center p-3 cursor-pointer"
            onClick={() => setSelectedPaymentMethod("credit-card")}
          >
            <input
              type="radio"
              id="credit-card"
              name="payment-method"
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              checked={selectedPaymentMethod === "credit-card"}
              onChange={() => setSelectedPaymentMethod("credit-card")}
            />
            <label
              htmlFor="credit-card"
              className="ml-2 block text-sm font-medium text-gray-700 flex-1 cursor-pointer">
              Cartão de Crédito
            </label>
            <div className={`transition-transform ${selectedPaymentMethod === "credit-card" ? 'rotate-90' : ''}`}>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {selectedPaymentMethod === "credit-card" && (
            <div className="bg-gray-50 p-3 border-t border-gray-200 animate-fadeIn">
              <div className="space-y-3">
                {savedCards.map((card) => (
                  <div
                    key={card.id}
                    className="flex items-center p-2 border border-gray-200 rounded-md bg-white hover:border-green-500 transition-colors">
                    <input
                      type="radio"
                      id={`card-${card.id}`}
                      name="cartaoSelecionado"
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      checked={selectedCard === card.id}
                      onChange={() => setSelectedCard(card.id)}
                    />
                    <label
                      htmlFor={`card-${card.id}`}
                      className="ml-2 flex items-center justify-between w-full cursor-pointer">
                      <div className="flex items-center">
                        <div
                          className={`w-10 h-6 rounded mr-2 flex items-center justify-center text-white text-xs ${
                            card.type === "visa"
                              ? "bg-blue-600"
                              : card.type === "mastercard"
                              ? "bg-red-600"
                              : "bg-gray-600"
                          }`}>
                          {card.type === "visa" ? "VISA" : "MC"}
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          •••• •••• •••• {card.last4}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        Validade: {card.expiry}
                      </span>
                    </label>
                  </div>
                ))}

                {showNewCardForm ? (
                  <CreditCardForm
                    onCancel={() => setShowNewCardForm(false)}
                    onSave={handleAddCard}
                  />
                ) : (
                  <div
                    className="flex items-center p-2 border border-gray-200 rounded-md bg-white cursor-pointer hover:border-green-500 transition-colors"
                    onClick={() => setShowNewCardForm(true)}>
                    <div className="ml-6 block text-sm font-medium text-green-600">
                      + Adicionar novo cartão
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Boleto Bancário */}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div 
            className="flex items-center p-3 cursor-pointer"
            onClick={() => setSelectedPaymentMethod("boleto")}
          >
            <input
              type="radio"
              id="boleto"
              name="payment-method"
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              checked={selectedPaymentMethod === "boleto"}
              onChange={() => setSelectedPaymentMethod("boleto")}
            />
            <label
              htmlFor="boleto"
              className="ml-2 block text-sm font-medium text-gray-700 flex-1 cursor-pointer">
              Boleto Bancário
            </label>
            <div className={`transition-transform ${selectedPaymentMethod === "boleto" ? 'rotate-90' : ''}`}>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {selectedPaymentMethod === "boleto" && (
            <div className="bg-gray-50 p-3 border-t border-gray-200 animate-fadeIn">
              <div className="flex flex-col items-center justify-center p-4">
                <div className="mb-4 p-2 bg-white rounded-lg shadow-xs">
                  <Barcode className="h-16 w-64 text-gray-800" />
                </div>
                <p className="text-sm text-gray-600 mb-4 text-center">
                  O boleto será gerado após a confirmação do pedido e poderá ser pago em qualquer banco ou casa lotérica.
                </p>
                <div className="bg-white p-3 rounded border border-gray-200 w-full">
                  <p className="text-xs text-gray-500 mb-1">Código de barras:</p>
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-sm bg-gray-100 p-2 rounded flex-1 mr-2">
                      {boletoCode}
                    </p>
                    <button 
                      className="p-2 text-gray-500 hover:text-green-600 transition-colors"
                      onClick={() => navigator.clipboard.writeText(boletoCode)}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 w-full bg-yellow-50 border border-yellow-100 rounded p-3">
                  <p className="text-xs text-yellow-800 text-center">
                    <span className="font-semibold">Vencimento:</span> {boletoExpiryDate} - Evite multas pagando até esta data
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pix */}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div 
            className="flex items-center p-3 cursor-pointer"
            onClick={() => setSelectedPaymentMethod("pix")}
          >
            <input
              type="radio"
              id="pix"
              name="payment-method"
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              checked={selectedPaymentMethod === "pix"}
              onChange={() => setSelectedPaymentMethod("pix")}
            />
            <label
              htmlFor="pix"
              className="ml-2 block text-sm font-medium text-gray-700 flex-1 cursor-pointer">
              Pix
            </label>
            <div className={`transition-transform ${selectedPaymentMethod === "pix" ? 'rotate-90' : ''}`}>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {selectedPaymentMethod === "pix" && (
            <div className="bg-gray-50 p-3 border-t border-gray-200 animate-fadeIn">
              <div className="flex flex-col items-center justify-center p-4">
                <div className="mb-4 p-4 bg-white rounded-lg shadow-xs border border-gray-200">
                  <QrCode className="h-32 w-32 text-green-600" />
                </div>
                <p className="text-sm text-gray-600 mb-4 text-center">
                  Escaneie o QR Code acima ou copie o código Pix abaixo para realizar o pagamento.
                </p>
                <div className="bg-white p-3 rounded border border-gray-200 w-full">
                  <p className="text-xs text-gray-500 mb-1">Chave Pix (copiar e colar):</p>
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-sm bg-gray-100 p-2 rounded flex-1 mr-2 break-all">
                      {pixCode}
                    </p>
                    <button 
                      className="p-2 text-gray-500 hover:text-green-600 transition-colors"
                      onClick={copyPixCode}
                    >
                      {copiedPix ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="mt-4 w-full bg-blue-50 border border-blue-100 rounded p-3">
                  <p className="text-xs text-blue-800 text-center">
                    <span className="font-semibold">Tempo restante:</span> {formatTime(pixExpiry)} - O código expira após este período
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente do formulário de cartão de crédito (mantido igual)
function CreditCardForm({ onCancel, onSave }) {
  const [formData, setFormData] = useState({
    cardNumber: "",
    cardName: "",
    expiry: "",
    cvv: "",
    saveCard: true,
  });

  const [errors, setErrors] = useState({
    cardNumber: "",
    cardName: "",
    expiry: "",
    cvv: "",
  });

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    return parts.length ? parts.join(" ") : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/[^0-9]/g, "");
    if (v.length >= 3) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let formattedValue = value;
    if (name === "cardNumber") {
      formattedValue = formatCardNumber(value);
    } else if (name === "expiry") {
      formattedValue = formatExpiry(value);
    } else if (name === "cvv") {
      formattedValue = value.replace(/[^0-9]/g, "").substring(0, 4);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validate = () => {
    const newErrors = {
      cardNumber: "",
      cardName: "",
      expiry: "",
      cvv: "",
    };

    let isValid = true;

    if (formData.cardNumber.replace(/\s/g, "").length !== 16) {
      newErrors.cardNumber = "Número do cartão inválido";
      isValid = false;
    }

    if (!formData.cardName.trim()) {
      newErrors.cardName = "Digite o nome como no cartão";
      isValid = false;
    }

    const expiryRegex = /^(0[1-9]|1[0-2])\/?([0-9]{2})$/;
    if (!expiryRegex.test(formData.expiry)) {
      newErrors.expiry = "Data inválida (MM/AA)";
      isValid = false;
    }

    if (!/^[0-9]{3,4}$/.test(formData.cvv)) {
      newErrors.cvv = "CVV inválido";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const cardType = getCardType(formData.cardNumber);
      onSave({
        ...formData,
        cardNumber: formData.cardNumber.replace(/\s/g, ""),
        cardType,
      });
    }
  };

  const getCardType = (number) => {
    const num = number.replace(/\s/g, "");
    if (/^4/.test(num)) return "visa";
    if (/^5[1-5]/.test(num)) return "mastercard";
    if (/^3[47]/.test(num)) return "amex";
    return "other";
  };

  const cardType = getCardType(formData.cardNumber);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="cardNumber"
          className="block text-sm font-medium text-gray-700 mb-1">
          Número do Cartão
        </label>
        <div className="relative">
          <input
            type="text"
            id="cardNumber"
            name="cardNumber"
            placeholder="0000 0000 0000 0000"
            className={`block w-full border ${
              errors.cardNumber ? "border-red-500" : "border-gray-300"
            } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            value={formData.cardNumber}
            onChange={handleChange}
            maxLength={19}
          />
          {cardType && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div
                className={`w-6 h-6 rounded flex items-center justify-center text-white text-xs ${
                  cardType === "visa"
                    ? "bg-blue-600"
                    : cardType === "mastercard"
                    ? "bg-red-600"
                    : "bg-gray-600"
                }`}>
                {cardType === "visa"
                  ? "V"
                  : cardType === "mastercard"
                  ? "MC"
                  : "••"}
              </div>
            </div>
          )}
        </div>
        {errors.cardNumber && (
          <p className="mt-1 text-sm text-red-600">{errors.cardNumber}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="cardName"
          className="block text-sm font-medium text-gray-700 mb-1">
          Nome no Cartão
        </label>
        <input
          type="text"
          id="cardName"
          name="cardName"
          placeholder="Nome como está no cartão"
          className={`block w-full border ${
            errors.cardName ? "border-red-500" : "border-gray-300"
          } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
          value={formData.cardName}
          onChange={handleChange}
        />
        {errors.cardName && (
          <p className="mt-1 text-sm text-red-600">{errors.cardName}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="expiry"
            className="block text-sm font-medium text-gray-700 mb-1">
            Validade (MM/AA)
          </label>
          <input
            type="text"
            id="expiry"
            name="expiry"
            placeholder="MM/AA"
            className={`block w-full border ${
              errors.expiry ? "border-red-500" : "border-gray-300"
            } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            value={formData.expiry}
            onChange={handleChange}
            maxLength={5}
          />
          {errors.expiry && (
            <p className="mt-1 text-sm text-red-600">{errors.expiry}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="cvv"
            className="block text-sm font-medium text-gray-700 mb-1">
            CVV
          </label>
          <div className="relative">
            <input
              type="text"
              id="cvv"
              name="cvv"
              placeholder="123"
              className={`block w-full border ${
                errors.cvv ? "border-red-500" : "border-gray-300"
              } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
              value={formData.cvv}
              onChange={handleChange}
              maxLength={4}
            />
          </div>
          {errors.cvv && (
            <p className="mt-1 text-sm text-red-600">{errors.cvv}</p>
          )}
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="saveCard"
          name="saveCard"
          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
          checked={formData.saveCard}
          onChange={handleChange}
        />
        <label htmlFor="saveCard" className="ml-2 block text-sm text-gray-700">
          Salvar cartão para compras futuras
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
          Salvar Cartão
        </button>
      </div>
    </form>
  );
}

export default PaymentSection;